import { useQuery } from "@tanstack/react-query";
import { fetchAllPages } from "@/lib/fetch-all-pages";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { useStoreContext } from "@/hooks/use-store-context";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, AlertTriangle, XCircle, Check } from "lucide-react";
import { formatPar, LOW_STOCK_THRESHOLD } from "@/lib/unit-utils";
import { formatQtyForProduct } from "@/lib/format-quantity";
import { cn } from "@/lib/utils";
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";

type LowStockItem = {
  id: string;
  productName: string;
  storeName: string;
  quantity: number;
  par: number | null;
  status: "low" | "out";
  updatedAt: string | null;
  product: { unit?: string | null; units_per_pack?: number | null } | null;
};

const DISMISS_KEY = "vrg_bell_dismissed_at";

function getDismissedAt(userId: string | undefined): number {
  try {
    const key = userId ? `${DISMISS_KEY}_${userId}` : DISMISS_KEY;
    const val = localStorage.getItem(key);
    return val ? parseInt(val, 10) : 0;
  } catch { return 0; }
}

function setDismissedAt(userId: string | undefined) {
  try {
    const key = userId ? `${DISMISS_KEY}_${userId}` : DISMISS_KEY;
    localStorage.setItem(key, Date.now().toString());
  } catch { /* ignore */ }
}

export function NotificationBell() {
  const { organization } = useOrganization();
  const { user } = useAuth();
  const { selectedStoreId, availableStores } = useStoreContext();
  const [open, setOpen] = useState(false);
  const [dismissedAt, setDismissedAtState] = useState(() => getDismissedAt(user?.id));

  const storeIds = selectedStoreId !== "all"
    ? [selectedStoreId]
    : availableStores.map((s) => s.id);

  const { data: allItems = [] } = useQuery<LowStockItem[]>({
    queryKey: ["bell-low-stock", organization?.id, storeIds.join(",")],
    queryFn: async () => {
      if (!organization || storeIds.length === 0) return [];
      const data = await fetchAllPages<unknown>(() =>
        supabase
          .from("store_products")
          .select("id, quantity_on_hand, par_level, reorder_method, min_order_threshold, store_id, updated_at, products(name, unit, pack_size, units_per_pack, sub_unit, sub_units_per_pack, units_per_sub_unit, display_tier, visibility), stores(name)")
          .in("store_id", storeIds)
          .is("hidden_at", null)
          .order("id")
      );

      const results: LowStockItem[] = [];
      for (const sp of data ?? []) {
        if ((sp as any).products?.visibility === 'archived') continue;
        // Skip catalog-hidden products from low-stock alerts (managed elsewhere)
        if ((sp as any).products?.visibility === 'hidden') continue;
        const qty = sp.quantity_on_hand ?? 0;
        const product = (sp as any).products;
        // Both par_level and min_order_threshold are stored in Unit 1.
        // Convert on-hand (eaches) to Unit 1 for comparison.
        const upp = product?.units_per_pack ?? 1;
        const qtyUnit1 = upp > 1 ? qty / upp : qty;
        let status: "low" | "out" | null = null;

        if (qty === 0) {
          status = "out";
        } else if (sp.reorder_method === "par_level") {
          // Par: low at 25% of par target (warning before stockout).
          const par = sp.par_level;
          if (par != null && par > 0 && qtyUnit1 <= par * LOW_STOCK_THRESHOLD) status = "low";
        } else {
          // Min order: low AT or BELOW threshold (threshold is the trigger, no buffer).
          const threshold = sp.min_order_threshold;
          if (threshold != null && threshold > 0 && qtyUnit1 <= threshold) status = "low";
        }

        if (status) {
          results.push({
            id: sp.id,
            productName: product?.name ?? "Unknown",
            storeName: (sp as any).stores?.name ?? "",
            quantity: qty,
            par: sp.reorder_method === "par_level" ? sp.par_level : sp.min_order_threshold,
            status,
            updatedAt: sp.updated_at ?? null,
            product: product ? { unit: product.unit, units_per_pack: product.units_per_pack } : null,
          });
        }
      }

      // Sort: out first, then low
      results.sort((a, b) => {
        if (a.status === "out" && b.status !== "out") return -1;
        if (a.status !== "out" && b.status === "out") return 1;
        return a.productName.localeCompare(b.productName);
      });

      return results;
    },
    enabled: !!organization && storeIds.length > 0,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Filter to only show items updated after the last dismiss
  const items = allItems.filter((item) => {
    if (!dismissedAt) return true;
    if (!item.updatedAt) return true;
    return new Date(item.updatedAt).getTime() > dismissedAt;
  });

  const markAllRead = useCallback(() => {
    setDismissedAt(user?.id);
    setDismissedAtState(Date.now());
  }, [user?.id]);

  const count = items.length;
  const outCount = items.filter((i) => i.status === "out").length;
  const lowCount = count - outCount;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <h4 className="text-sm font-semibold">Stock Alerts</h4>
          {count > 0 && (
            <Button variant="ghost" size="sm" className="h-9 text-sm gap-1" onClick={markAllRead}>
              <Check className="h-3 w-3" /> Mark all read
            </Button>
          )}
        </div>
        {count > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/30">
            {outCount > 0 && <span className="text-xs text-destructive font-medium">{outCount} out</span>}
            {outCount > 0 && lowCount > 0 && <span className="text-xs text-muted-foreground">·</span>}
            {lowCount > 0 && <span className="text-xs text-warning font-medium">{lowCount} low</span>}
          </div>
        )}
        <div className="max-h-80 overflow-y-auto">
          {count === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">All stock levels OK</p>
          ) : (
            items.slice(0, 25).map((item) => (
              <Link
                key={item.id}
                to={`/dashboard/inventory?status=${item.status === "out" ? "out" : "low"}`}
                onClick={() => setOpen(false)}
                className="flex items-start gap-2.5 border-b px-3 py-2.5 text-sm last:border-0 hover:bg-accent/50 transition-colors"
              >
                {item.status === "out" ? (
                  <XCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug font-medium truncate">{item.productName}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {item.storeName} · {formatQtyForProduct(item.quantity, item.product)} on hand{item.par != null ? ` / ${formatPar(item.par, item.product)} par` : ""}
                  </p>
                </div>
                <span className={cn(
                  "shrink-0 text-[10px] font-semibold uppercase mt-0.5 px-1.5 py-0.5 rounded",
                  item.status === "out" ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning"
                )}>
                  {item.status}
                </span>
              </Link>
            ))
          )}
          {count > 25 && (
            <Link
              to="/dashboard/inventory?status=low"
              onClick={() => setOpen(false)}
              className="block text-center text-xs text-primary py-2 hover:underline border-t"
            >
              View all {count} items →
            </Link>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
